import { useRef, useState } from 'react';

import { IS_CLOUD } from '@qality/common-config';
import { IssuesAndTotal, JQLFilters } from '@qality/common-model/src/JQLSearch';
import { useTestCaseRepositoryFiltersContext } from '@qality/common-pages/src/TestCaseRepositoryPage';
import { FlagService, StorageOfUniqueServices } from '@qality/common-service';

export default function useTestCaseSearchPaginated() {
  const { isFilterApplied } = useTestCaseRepositoryFiltersContext();
  const [isLoading, setIsLoading] = useState(true);
  const [nextPageTokenForSearchByText, setNextPageTokenForSearchByText] =
    useState<string>(null);
  const [nextPageTokenForSearchByKeys, setNextPageTokenForSearchByKeys] =
    useState<string>(null);
  const pageNumberRef = useRef<number>(0);

  const resetPageableTokens = () => {
    if (IS_CLOUD) {
      setNextPageTokenForSearchByText(null);
      setNextPageTokenForSearchByKeys(null);
    }
  };

  const getNextTestCasesPage = async (
    pageSize: number,
    filters: JQLFilters,
    shouldFetchFirst = false
  ): Promise<IssuesAndTotal> => {
    try {
      setIsLoading(true);

      if (IS_CLOUD) {
        const {
          issues,
          total,
          nextPageTokenForSearchByText: tokenForSearchByText,
          nextPageTokenForSearchByKeys: tokenForSearchByKeys,
        } = await StorageOfUniqueServices.get(
          'TestCaseRepositoryService'
        ).getQAlityFilteredTestCasesCloud({
          filters,
          ...(isFilterApplied && !shouldFetchFirst
            ? {
                nextPageTokenForSearchByText,
                nextPageTokenForSearchByKeys,
              }
            : {}),
          maxResults: pageSize,
        });

        setNextPageTokenForSearchByText(tokenForSearchByText);
        setNextPageTokenForSearchByKeys(tokenForSearchByKeys);
        setIsLoading(false);
        return { issues, total };
      } else {
        const issuesAndTotal = await StorageOfUniqueServices.get(
          'TestCaseRepositoryService'
        ).getQAlityFilteredTestCasesServer({
          filters,
          maxResults: pageSize,
          startAt: pageNumberRef.current * pageSize,
        });

        setIsLoading(false);

        return issuesAndTotal;
      }
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't get test cases. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getNextTestCasesPage,
    isLoading,
    resetPageableTokens,
    pageNumberRef,
  };
}
